import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationType } from 'src/@smartmanufacturing/common/enum';
import { LangLoaderService } from 'src/app/services/lang-loader.service';
import { AdminAccessApprovalService } from 'src/backend/services/admin-access-approval/admin-access-approval.service';

@Component({
  selector: 'app-admin-access-notification-actions',
  templateUrl: './admin-access-notification-actions.component.html',
  styleUrls: ['./admin-access-notification-actions.component.scss'],
})
export class AdminAccessNotificationActionsComponent implements OnInit, OnDestroy {
  @Input() message: any;
  @Output() actionDone = new EventEmitter<any>();

  languageDir: 'ltr' | 'rtl' = 'ltr';
  langObj: any = {};

  private readonly destroy$ = new Subject<void>();

  constructor(
    private service: AdminAccessApprovalService,
    private toaster: ToastrService,
    private spinner: NgxSpinnerService,
    private lang: LangLoaderService,
  ) {
    this.lang.onLangChange$.pipe(takeUntil(this.destroy$)).subscribe(() => this.reloadLang());
  }

  ngOnInit(): void {
    this.reloadLang();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Gate used by the parent template to decide whether to mount this component.
  static isAdminAccessRequest(message: any): boolean {
    return message?.notificationType === NotificationType.ADMIN_ACCESS_REQUEST;
  }

  private reloadLang(): void {
    this.lang.loadForCurrentMenu().subscribe(({ langObj, languageDir }) => {
      this.langObj = langObj;
      this.languageDir = languageDir;
    });
  }

  onApprove(event: Event): void {
    event?.stopPropagation();
    const id = this.parseRequestId();
    if (!id) {
      this.toaster.error(this.lang.t(this.langObj, 'CouldNotResolveRequestRef', 'Could not resolve the request reference.'));
      return;
    }
    Swal.fire({
      title: this.lang.t(this.langObj, 'ApproveRequest', 'Approve request?'),
      text: this.lang.t(this.langObj, 'ApproveRequestText', 'The requesting admin will be able to access your account within the validity window.'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#5D9C59',
      confirmButtonText: this.lang.t(this.langObj, 'Approve', 'Approve'),
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.spinner.show();
      this.service.approve(id).subscribe({
        next: (res) => {
          this.spinner.hide();
          this.toaster.success(res?.message ?? this.lang.t(this.langObj, 'RequestApproved', 'Request approved.'));
          this.actionDone.emit(this.message);
        },
        error: ({ error }) => {
          this.spinner.hide();
          this.toaster.error(error?.message ?? this.lang.t(this.langObj, 'FailedToApprove', 'Failed to approve the request.'));
        }
      });
    });
  }

  onReject(event: Event): void {
    event?.stopPropagation();
    const id = this.parseRequestId();
    if (!id) {
      this.toaster.error(this.lang.t(this.langObj, 'CouldNotResolveRequestRef', 'Could not resolve the request reference.'));
      return;
    }
    Swal.fire({
      title: this.lang.t(this.langObj, 'RejectRequest', 'Reject request?'),
      text: this.lang.t(this.langObj, 'RejectRequestText', 'The requesting admin will not be allowed to access your account.'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#F55050',
      confirmButtonText: this.lang.t(this.langObj, 'Reject', 'Reject'),
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.spinner.show();
      this.service.reject(id).subscribe({
        next: (res) => {
          this.spinner.hide();
          this.toaster.success(res?.message ?? this.lang.t(this.langObj, 'RequestRejected', 'The request has been rejected.'));
          this.actionDone.emit(this.message);
        },
        error: ({ error }) => {
          this.spinner.hide();
          this.toaster.error(error?.message ?? this.lang.t(this.langObj, 'FailedToReject', 'Failed to reject the request.'));
        }
      });
    });
  }

  private parseRequestId(): number | null {
    const raw = this.message?.elementId;
    if (!raw) return null;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  }
}
